import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PlaceDetails {
  name: string;
  placeId: string;
  lat: number;
  lng: number;
}

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string, details?: PlaceDetails) => void;
  placeholder?: string;
  types?: string[];
  id?: string;
  required?: boolean;
  className?: string;
  icon?: ReactNode;
}

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

function loadGoogleMapsScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
}

export function PlacesAutocomplete({
  value,
  onChange,
  placeholder,
  types,
  id,
  required,
  className,
  icon,
}: PlacesAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dummyDiv = useRef<HTMLDivElement | null>(null);

  const loadingRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (ready || loadingRef.current) return;
    loadingRef.current = true;
    loadGoogleMapsScript().then(() => {
      autocompleteService.current = new google.maps.places.AutocompleteService();
      if (!dummyDiv.current) {
        dummyDiv.current = document.createElement("div");
      }
      placesService.current = new google.maps.places.PlacesService(dummyDiv.current);
      setReady(true);
    });
  }, [ready]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cost control: one session token per typing interaction (Google bills the
  // whole session once) + 300 ms debounce so not every keystroke is a request.
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const runFetch = useCallback(
    (input: string) => {
      if (!ready || !autocompleteService.current || input.length < 3) {
        setSuggestions([]);
        return;
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }
      const request: google.maps.places.AutocompletionRequest = {
        input,
        componentRestrictions: { country: "de" },
        sessionToken: sessionTokenRef.current,
      };
      if (types?.length) {
        (request as any).types = types;
      }
      const reqId = ++requestIdRef.current;
      autocompleteService.current.getPlacePredictions(request, (results, status) => {
        if (reqId !== requestIdRef.current) return; // stale response
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setSuggestions(results);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
        }
      });
    },
    [ready, types]
  );

  const fetchSuggestions = useCallback(
    (input: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (input.length < 3) {
        setSuggestions([]);
        return;
      }
      debounceRef.current = setTimeout(() => runFetch(input), 300);
    },
    [runFetch]
  );

  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    setShowDropdown(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const name = prediction.structured_formatting.main_text;
    const sessionToken = sessionTokenRef.current ?? undefined;
    sessionTokenRef.current = null; // retire token after selection

    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ["geometry", "name", "place_id"], sessionToken },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            onChange(name, {
              name,
              placeId: prediction.place_id,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            });
          } else {
            onChange(name);
          }
        }
      );
    } else {
      onChange(name);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {icon && (
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground">
          {icon}
        </div>
      )}
      <Input
        id={id}
        value={value}
        onChange={(e) => {
          ensureLoaded();
          onChange(e.target.value);
          fetchSuggestions(e.target.value);
        }}
        onFocus={() => {
          ensureLoaded();
          if (suggestions.length > 0) setShowDropdown(true);
        }}
        placeholder={placeholder}
        required={required}
        className={cn("text-sm", icon && "pl-9", className)}
        autoComplete="off"
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          {suggestions.map((s) => (
            <li
              key={s.place_id}
              className="cursor-pointer px-3 py-2 text-sm text-popover-foreground transition-colors hover:bg-accent/20"
              onMouseDown={() => handleSelect(s)}
            >
              <span className="font-medium">{s.structured_formatting.main_text}</span>
              {s.structured_formatting.secondary_text && (
                <span className="ml-1 text-muted-foreground">
                  — {s.structured_formatting.secondary_text}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
